import { Banner, Button, Field, Input, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

type TextLayer = { type: 'text'; text: string; x: number; y: number; size: number; color: string; weight: 'normal' | 'bold'; align: 'left' | 'center' | 'right'; maxWidth: number };
type QrLayer = { type: 'qr'; value: string; x: number; y: number; size: number; color: string; background: string };
type Layer = TextLayer | QrLayer;
interface DynImage { id: string; name: string; key: string; backgroundUrl: string; width: number; height: number; layers: Layer[] }
interface Draft { id?: string; name: string; key?: string; backgroundUrl: string; width: number; height: number; layers: Layer[] }

const newText = (): TextLayer => ({ type: 'text', text: 'Olá, {{primeiro_nome}}!', x: 0.5, y: 0.5, size: 64, color: '#ffffff', weight: 'bold', align: 'center', maxWidth: 0.9 });
const newQr = (): QrLayer => ({ type: 'qr', value: '{{cupom:exemplo}}', x: 0.85, y: 0.85, size: 240, color: '#000000', background: '#ffffff' });
const VARS = ['{{nome}}', '{{primeiro_nome}}', '{{telefone}}', '{{cupom:chave}}', '{{campo:chave}}'];

const WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Negrito' },
];
const ALIGN_OPTIONS = [
  { value: 'left', label: 'Esquerda' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Direita' },
];

/** Ferramentas › Imagens dinâmicas */
export function ImagensDinamicasPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [images, setImages] = useState<DynImage[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [active, setActive] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<DynImage | null>(null);
  const stage = useRef<HTMLDivElement>(null);
  const bgInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/dynamic-images');
    if (r.ok) setImages((await r.json()).images);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('auth_token');
    const r = await fetch('/api/media/upload', { method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(data.error || 'Erro no upload'); return; }
    const img = new Image();
    img.onload = () => setDraft((d) => ({ ...(d ?? { name: '', layers: [newText()] }), backgroundUrl: data.fileUrl, width: img.naturalWidth, height: img.naturalHeight }));
    img.src = data.fileUrl;
    setPreview(null);
  };

  const patchLayer = (i: number, patch: Partial<Layer>) => {
    setDraft((d) => d && { ...d, layers: d.layers.map((l, j) => (j === i ? ({ ...l, ...patch } as Layer) : l)) });
    setPreview(null);
  };

  /** Arrasta a camada sobre o fundo (posição relativa 0–1) */
  const drag = (i: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    setActive(i);
    const box = stage.current?.getBoundingClientRect();
    if (!box) return;
    const move = (ev: PointerEvent) => {
      const x = Math.min(1, Math.max(0, (ev.clientX - box.left) / box.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - box.top) / box.height));
      patchLayer(i, { x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const runPreview = async () => {
    if (!draft) return;
    setBusy(true);
    const r = await fetchApi('/api/dynamic-images/preview', { method: 'POST', body: JSON.stringify({ backgroundUrl: draft.backgroundUrl, layers: draft.layers }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível gerar a prévia'); return; }
    setPreview(j.url);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    const body = JSON.stringify({ name: draft.name, key: draft.key, backgroundUrl: draft.backgroundUrl, layers: draft.layers });
    const r = await fetchApi(draft.id ? `/api/dynamic-images/${draft.id}` : '/api/dynamic-images', { method: draft.id ? 'PUT' : 'POST', body });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar'); return; }
    toast.success(`Imagem salva: use {{imagem:${j.image.key}}}`);
    setDraft(null);
    load();
  };

  const remove = async (img: DynImage) => {
    const r = await fetchApi(`/api/dynamic-images/${img.id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Imagem excluída'); load(); } else toast.error('Não foi possível excluir');
  };

  const layer = draft?.layers[active];

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/ferramentas" className="tools-back"><ICONS.ArrowLeft size={16} /> Ferramentas</Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Imagens dinâmicas</h2>
            <div style={{ color: 'var(--text-muted)' }}>
              Envie a sua arte e escreva por cima o nome do contato, o cupom ou um QR Code. No envio cada contato recebe a própria imagem:
              use <code>{'{{imagem:chave}}'}</code> no lugar da URL da mídia na campanha, na sequência ou no bloco de imagem do fluxo.
            </div>
          </div>
          {canEdit && !draft && <Button variant="primary" icon={ICONS.Plus} onClick={() => { setDraft({ name: '', backgroundUrl: '', width: 0, height: 0, layers: [newText()] }); setActive(0); setPreview(null); }}>Nova imagem</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!draft && images && images.length === 0 && <Banner variant="default" description={<>Nenhuma imagem ainda. Crie uma a partir de uma arte (PNG ou JPG, até 4000 × 4000 px).</>} />}

        {!draft && images && images.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {images.map((img) => (
              <section key={img.id} className="brand-card space-y-2">
                <img src={img.backgroundUrl} alt="" style={{ width: '100%', aspectRatio: `${img.width} / ${img.height}`, objectFit: 'cover', borderRadius: 6 }} />
                <div className="font-semibold">{img.name}</div>
                <div className="text-xs !text-[var(--text-muted)]"><code>{`{{imagem:${img.key}}}`}</code> · {img.width}×{img.height}</div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button variant="secondary" icon={ICONS.Edit} onClick={() => { setDraft({ ...img }); setActive(0); setPreview(null); }}>Editar</Button>
                    <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(img)}>Excluir</DangerButton>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        {draft && (
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(280px, 2fr)' }}>
            <section className="brand-card space-y-3">
              {!draft.backgroundUrl ? (
                <label className="block text-center" style={{ padding: 48, border: '2px dashed var(--border-strong)', borderRadius: 8, cursor: 'pointer' }}>
                  <ICONS.Upload size={24} />
                  <div>Escolha a arte de fundo (PNG ou JPG)</div>
                  <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                </label>
              ) : preview ? (
                <img src={preview} alt="Prévia" style={{ width: '100%', borderRadius: 6 }} />
              ) : (
                <div ref={stage} style={{ position: 'relative', width: '100%', aspectRatio: `${draft.width} / ${draft.height}`, backgroundImage: `url("${draft.backgroundUrl}")`, backgroundSize: '100% 100%', borderRadius: 6, overflow: 'hidden', touchAction: 'none' }}>
                  {draft.layers.map((l, i) => {
                    const scale = (stage.current?.clientWidth ?? draft.width) / draft.width;
                    const common: React.CSSProperties = { position: 'absolute', left: `${l.x * 100}%`, top: `${l.y * 100}%`, cursor: 'move', outline: i === active ? '2px dashed var(--brand-text)' : '1px dashed rgba(0,0,0,.35)' };
                    return l.type === 'qr' ? (
                      <div key={i} onPointerDown={drag(i)} style={{ ...common, width: l.size * scale, height: l.size * scale, transform: 'translate(-50%, -50%)', background: l.background, display: 'grid', placeItems: 'center', fontSize: 11, color: l.color }}>QR</div>
                    ) : (
                      <div key={i} onPointerDown={drag(i)} style={{ ...common, transform: `translate(${l.align === 'left' ? '0' : l.align === 'right' ? '-100%' : '-50%'}, -50%)`, maxWidth: `${l.maxWidth * 100}%`, color: l.color, fontSize: l.size * scale, fontWeight: l.weight === 'bold' ? 700 : 400, textAlign: l.align, lineHeight: 1.2, whiteSpace: 'pre-wrap', fontFamily: 'Inter, sans-serif' }}>{l.text || ' '}</div>
                    );
                  })}
                </div>
              )}
              {draft.backgroundUrl && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" icon={ICONS.Plus} onClick={() => { setDraft({ ...draft, layers: [...draft.layers, newText()] }); setActive(draft.layers.length); setPreview(null); }}>Texto</Button>
                  <Button variant="secondary" icon={ICONS.Plus} onClick={() => { setDraft({ ...draft, layers: [...draft.layers, newQr()] }); setActive(draft.layers.length); setPreview(null); }}>QR Code</Button>
                  <Button variant="secondary" icon={ICONS.Upload} onClick={() => bgInput.current?.click()}>Trocar fundo</Button>
                  <input ref={bgInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                  <Button variant="secondary" icon={preview ? ICONS.Edit : ICONS.Visibility} loading={busy && !preview} onClick={() => (preview ? setPreview(null) : runPreview())}>{preview ? 'Voltar a editar' : 'Prévia real'}</Button>
                </div>
              )}
              <div className="text-xs !text-[var(--text-muted)]">Arraste as camadas sobre a arte. A prévia real usa o contato de exemplo Maria Souza e o cupom CUPOM123.</div>
            </section>

            <section className="brand-card space-y-4">
              <Field label="Nome">
                <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} placeholder="Convite Black Friday" />
              </Field>
              {draft.id ? <div className="text-xs !text-[var(--text-muted)]">Variável: <code>{`{{imagem:${draft.key}}}`}</code> (a chave não muda)</div>
                : (
                  <Field label="Chave (opcional)" description="Vira {{imagem:chave}}; se vazia, sai do nome">
                    <Input onChange={(e) => setDraft({ ...draft, key: e.target.value })} value={draft.key ?? ''} />
                  </Field>
                )}

              {draft.layers.length > 0 && (
                <Field label="Camada">
                  <Select value={({ value: String(active), label: `${active + 1}. ${draft.layers[active]?.type === 'qr' ? `QR: ${(draft.layers[active] as QrLayer).value}` : (draft.layers[active] as TextLayer).text.slice(0, 30)}` })?.value ?? null} items={draft.layers.map((l, i) => ({ value: String(i), label: `${i + 1}. ${l.type === 'qr' ? `QR: ${l.value}` : l.text.slice(0, 30)}` }))} onValueChange={(value: string | null) => setActive(Number(value))} />
                </Field>
              )}

              {layer?.type === 'text' && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Texto</h3>
                  <Field label="Conteúdo">
                    <Input onChange={(e) => patchLayer(active, { text: e.target.value })} value={layer.text} />
                  </Field>
                  <div className="text-xs !text-[var(--text-muted)]">Variáveis: {VARS.map((v) => <code key={v} style={{ marginRight: 6 }}>{v}</code>)}</div>
                  <div className="grid gap-3 grid-cols-2">
                    <Field label="Tamanho (px)">
                      <Input onChange={(e) => patchLayer(active, { size: Number(e.target.value) })} type="number" value={String(layer.size)} />
                    </Field>
                    <ColorField label="Cor" value={layer.color} onChange={(v) => patchLayer(active, { color: v })} />
                    <Field label="Peso">
                      <Select value={(WEIGHT_OPTIONS.find((o) => o.value === layer.weight) ?? null)?.value ?? null} items={WEIGHT_OPTIONS} onValueChange={(value: string | null) => patchLayer(active, { weight: value as TextLayer['weight'] })} />
                    </Field>
                    <Field label="Alinhamento">
                      <Select value={(ALIGN_OPTIONS.find((o) => o.value === layer.align) ?? null)?.value ?? null} items={ALIGN_OPTIONS} onValueChange={(value: string | null) => patchLayer(active, { align: value as TextLayer['align'] })} />
                    </Field>
                  </div>
                  <Field label="Largura máxima (% da imagem)">
                    <Input onChange={(e) => patchLayer(active, { maxWidth: Math.min(100, Math.max(5, Number(e.target.value))) / 100 })} type="number" value={String(Math.round(layer.maxWidth * 100))} />
                  </Field>
                </div>
              )}
              {layer?.type === 'qr' && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>QR Code</h3>
                  <Field label="Conteúdo do QR" description="Link ou código; aceita variáveis como {{cupom:chave}}">
                    <Input onChange={(e) => patchLayer(active, { value: e.target.value })} value={layer.value} />
                  </Field>
                  <div className="grid gap-3 grid-cols-3">
                    <Field label="Tamanho (px)">
                      <Input onChange={(e) => patchLayer(active, { size: Number(e.target.value) })} type="number" value={String(layer.size)} />
                    </Field>
                    <ColorField label="Cor" value={layer.color} onChange={(v) => patchLayer(active, { color: v })} />
                    <ColorField label="Fundo" value={layer.background} onChange={(v) => patchLayer(active, { background: v })} />
                  </div>
                </div>
              )}
              {layer && (
                <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => { setDraft({ ...draft, layers: draft.layers.filter((_, j) => j !== active) }); setActive(0); setPreview(null); }}>Remover camada</DangerButton>
              )}

              <div className="flex justify-end gap-2" style={{ paddingTop: 8 }}>
                <Button variant="secondary" onClick={() => setDraft(null)}>Cancelar</Button>
                <Button variant="primary" loading={busy} disabled={!draft.name.trim() || !draft.backgroundUrl} onClick={save}>Salvar</Button>
              </div>
            </section>
          </div>
        )}
      </div>

      <DangerConfirmModal open={!!deleting} title="Excluir imagem dinâmica" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) await remove(deleting); setDeleting(null); }}>
        Mensagens com <code>{`{{imagem:${deleting?.key}}}`}</code> deixam de encontrar a imagem. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <div className="font-semibold" style={{ fontSize: 13, marginBottom: 4 }}>{label}</div>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
        style={{ width: '100%', height: 36, border: '1px solid var(--text-faint)', borderRadius: 6, padding: 2, background: 'var(--surface)', cursor: 'pointer' }} />
    </label>
  );
}
